import { useFindByQuery } from '@gripp/shared-logic';
import { Colors, MobileIcon } from '@gripp/shared-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

export type WebTagPickerProps = {
  setTag: (tag: any) => void;
  setAlertTitle?: (title: string) => void;
  setShowAlert?: (visible: boolean) => void;
};

export const WebTagPicker = (props: WebTagPickerProps) => {
  // Same hook pattern as mobile version
  const useTagByCode = (tagCode: string) => {
    const [tag, setTag] = useState<any>(undefined);

    const { data, isLoading } = useFindByQuery({
      modelName: 'tag',
      query: FIND_TAG_BY_TAGCODE,
      variables: { tagCode },
      options: {
        staleTime: 0,
        gcTime: 0,
      },
    });

    useEffect(() => {
      if (!data) return;
      setTag(data.items[0]);
    }, [data]);

    return { tag, isLoading };
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [tagCode, setTagCode] = useState<string>('');
  const { tag, isLoading } = useTagByCode(tagCode);
  const { t } = useTranslation();

  useEffect(() => {
    if (tagCode === '' || isLoading) return;

    if (tag?.tagCode === tagCode) {
      if (tag?.status === 'ACTIVE' && tag?.link?.linkData) {
        props.setAlertTitle &&
          props.setAlertTitle(t('asset.customAlert.tryAnotherTag'));
        props.setShowAlert && props.setShowAlert(true);
      } else {
        props.setTag({ id: tag.id, tagCode: tag.tagCode });
      }
      setTagCode('');
      setModalVisible(false);
    }
  }, [tag, tagCode, isLoading]);

  const handleSubmit = () => {
    if (tagCode.trim()) {
      // Trigger the tag lookup
      setTagCode(tagCode.trim());
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setTagCode('');
  };

  return (
    <>
      <TouchableOpacity onPress={() => setModalVisible(true)}>
        <View style={styles.pickerNew}>
          <MobileIcon size={24} color={Colors.role.textStrong} />
        </View>
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={handleCancel}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('asset.enterTagCode')}</Text>

            <TextInput
              style={styles.input}
              value={tagCode}
              onChangeText={setTagCode}
              placeholder={t('asset.tagCodePlaceholder')}
              placeholderTextColor={Colors.role.textSecondary}
              autoFocus={true}
              onSubmitEditing={handleSubmit}
            />

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={handleCancel}
              >
                <Text style={styles.cancelButtonText}>{t('asset.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.submitButton,
                  !tagCode.trim() && styles.submitButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={!tagCode.trim()}
              >
                <Text style={styles.submitButtonText}>{t('asset.add')}</Text>
              </TouchableOpacity>
            </View>

            {isLoading && (
              <Text style={styles.loadingText}>{t('asset.lookingUpTag')}</Text>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
};

const FIND_TAG_BY_TAGCODE = `
  query FindTags($tagCode: String) {
    findTags(filter: {
      tagCode: { eq: $tagCode}
    }) {
      items {
        id
        tagCode
        status
      }
    }
  }
`;

const styles = StyleSheet.create({
  pickerNew: {
    width: 88,
    height: 88,
    borderRadius: 4,
    backgroundColor: Colors.role.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
  },
  picker: {
    alignSelf: 'center',
    height: 80,
    width: 80,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.role.surface,
    borderWidth: 1,
    borderColor: Colors.role.borderControl,
  },
  plusIcon: {
    fontSize: 32,
    color: Colors.primaryDark,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: Colors.role.surface,
    borderRadius: 8,
    padding: 20,
    width: 400,
    maxWidth: '90%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
    color: Colors.role.textStrong,
  },
  input: {
    borderWidth: 1,
    borderColor: Colors.role.borderControl,
    borderRadius: 4,
    padding: 12,
    fontSize: 16,
    marginBottom: 20,
    color: Colors.role.textStrong,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  cancelButton: {
    flex: 1,
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.role.borderControl,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: Colors.role.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {
    flex: 1,
    padding: 12,
    borderRadius: 4,
    backgroundColor: Colors.primary,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: Colors.role.fillMuted,
  },
  submitButtonText: {
    color: Colors.role.textOnBrand,
    fontSize: 16,
    fontWeight: '600',
  },
  loadingText: {
    textAlign: 'center',
    marginTop: 10,
    color: Colors.role.textSecondary,
    fontSize: 14,
  },
});
